import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RegsApiService } from '../../data/regs-api.service';
import { FollowResult, RegsFollowService } from '../../data/regs-follow.service';
import { RegsChoiceService } from '../../data/regs-choice.service';
import {
  Species,
  Sector,
  Waters,
  Rule,
  Season,
  LiveData,
  Lookalike,
  regsStateName,
  regsPair,
  regsConfirmText,
} from '../../domain/regs-models';
import {
  Verdict,
  VerdictKind,
  verdictFor,
  answerSize,
  SizeLimit,
  mdLabel,
  parseAsk,
} from '../../domain/regs-answer';
import { SizeRulerComponent } from '../widgets/size-ruler.component';
import { WeekStripComponent } from '../widgets/week-strip.component';
import { SeasonStripComponent } from '../widgets/season-strip.component';
import { QuotaBarComponent } from '../widgets/quota-bar.component';
import { KeepItFollowSheetComponent } from '../keep-it-follow-sheet/keep-it-follow-sheet.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-keep-it-answer-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SizeRulerComponent,
    WeekStripComponent,
    SeasonStripComponent,
    QuotaBarComponent,
    KeepItFollowSheetComponent,
  ],
  template: `
    <div class="answer-page">
      <!-- Sheet Header -->
      <div class="sheet-header">
        <button type="button" class="round-btn back-btn" (click)="onBack()" title="Back">
          <span class="material-icons">chevron_left</span>
        </button>
        <div class="header-titles">
          <h2 class="title">{{ species?.short || 'Loading...' }}</h2>
          <div class="subtitle">{{ waters.label }} · {{ sector === 'commercial' ? 'Commercial' : 'Recreational' }}</div>
        </div>
        <div class="header-actions">
          <button
            *ngIf="species"
            type="button"
            class="follow-pill"
            [class.following]="isFollowing"
            (click)="openFollowModal()"
          >
            <span class="material-icons pill-icon">{{ isFollowing ? 'notifications_active' : 'notifications_none' }}</span>
            <span>{{ isFollowing ? 'Following' : 'Follow' }}</span>
          </button>
          <button type="button" class="round-btn close-btn" (click)="onClose()" title="Close">
            <span class="material-icons">close</span>
          </button>
        </div>
      </div>

      <!-- Main Scrollable Body -->
      <div class="body-scroll" *ngIf="species; else loadingOrError">
        <!-- User Question Bubble -->
        <div class="user-bubble-container">
          <div class="user-bubble">
            {{ question || defaultQuestion }}
          </div>
        </div>

        <!-- Skipper Says -->
        <div class="skipper-says-card">
          <div class="skipper-avatar">
            <img src="assets/skipper/skipper_avatar.png" alt="Skipper" (error)="avatarFallback($event)" />
          </div>
          <div class="skipper-content">
            <div class="skipper-name">Skipper</div>
            <div class="skipper-text">{{ verdictSays }}</div>
          </div>
        </div>

        <!-- Area variation note -->
        <div *ngIf="areaSays" class="skipper-says-card area-card">
          <div class="skipper-avatar">
            <span class="material-icons area-icon">near_me</span>
          </div>
          <div class="skipper-content">
            <div class="skipper-text">{{ areaSays }}</div>
          </div>
        </div>

        <!-- Intro follow-up note -->
        <div *ngIf="intro" class="skipper-says-card intro-card">
          <div class="skipper-content">
            <div class="skipper-text">{{ intro }}</div>
          </div>
        </div>

        <!-- Follow Result or Offer -->
        <div *ngIf="followFeedback" class="skipper-says-card feedback-card">
          <div class="skipper-content">
            <div class="skipper-text">{{ followFeedback }}</div>
          </div>
        </div>

        <div *ngIf="!isFollowing && !offerDismissed && !followFeedback" class="skipper-says-card offer-card">
          <div class="skipper-avatar">
            <img src="assets/skipper/skipper_avatar.png" alt="Skipper" (error)="avatarFallback($event)" />
          </div>
          <div class="skipper-content">
            <div class="skipper-text">
              Want me to keep watch? I’ll tell you when {{ liveData?.hasQuotaNumbers ? 'the rules, season or quota change' : 'the rules or season change' }}.
            </div>
            <div class="offer-actions">
              <button type="button" class="btn-follow" (click)="openFollowModal()">
                <span class="material-icons">notification_add</span>
                <span>Follow</span>
              </button>
              <button type="button" class="btn-not-now" (click)="dismissOffer()">Not now</button>
            </div>
          </div>
        </div>

        <!-- Main Rule Details Box -->
        <div class="card-box rule-details-card" *ngIf="activeRule">
          <!-- Verdict Band -->
          <div class="verdict-band" [ngClass]="verdictBandClass">
            <div class="verdict-label">{{ currentVerdict.label }}</div>
            <div *ngIf="currentVerdict.big" class="big-verdict-row">
              <span class="big-number">{{ currentVerdict.big }}</span>
              <span class="unit-text">{{ currentVerdict.unit }}</span>
            </div>
            <div *ngIf="!currentVerdict.big" class="verdict-message">
              {{ verdictFallbackText }}
            </div>
            <div *ngIf="currentVerdict.detail && currentVerdict.kind !== 'info'" class="verdict-detail">
              {{ currentVerdict.detail }}
            </div>
          </div>

          <!-- Amber Note -->
          <div *ngIf="currentVerdict.note" class="amber-note">
            <span class="material-icons amber-icon">info</span>
            <span>{{ currentVerdict.note }}</span>
          </div>

          <!-- Size Limits Section & Visual Ruler -->
          <div *ngIf="hasSizeLimits" class="section-row">
            <div class="section-label">Size</div>
            <app-size-ruler
              [min]="minLimit"
              [max]="maxLimit"
              [askedInches]="askedInches ?? null"
            ></app-size-ruler>
            <div *ngIf="sizeDescription" class="size-text-sub">
              {{ sizeDescription }}
            </div>
          </div>

          <!-- Week Retention Strip -->
          <div *ngIf="showWeekStrip" class="section-row">
            <div class="section-label">This week</div>
            <app-week-strip [rule]="activeRule" [todayDate]="njTodayDate"></app-week-strip>
          </div>

          <!-- Season & Bag Periods Strip -->
          <div *ngIf="activeRule.seasons && activeRule.seasons.length > 0" class="section-row">
            <div class="section-label">Season and bag</div>
            <app-season-strip
              [seasons]="activeRule.seasons"
              [todayMd]="species.todayMd"
            ></app-season-strip>
            <div class="periods-list">
              <div *ngFor="let s of sortedSeasons" class="period-item" [class.is-now]="isSeasonNow(s)">
                <div class="period-dates">{{ formatSeasonDates(s) }}</div>
                <div class="period-label">{{ s.label || 'Open' }}</div>
                <div class="period-badges">
                  <span *ngIf="s.closed" class="badge badge-closed">Closed</span>
                  <span *ngIf="isSeasonNow(s)" class="badge badge-now">Now</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Live Quota Section -->
          <div *ngIf="liveData && (liveData.rows.length > 0 || liveData.sources.length > 0)" class="section-row">
            <div class="section-label">
              {{ liveData.hasQuotaNumbers ? 'Quota left' : 'Category status' }}
            </div>
            <div class="quota-rows">
              <div *ngFor="let r of liveData.rows" class="quota-row-item">
                <div class="quota-row-top">
                  <span class="quota-row-label">{{ r.label }}</span>
                  <span *ngIf="r.status" class="pill" [ngClass]="isClosedStatus(r.status) ? 'pill-shut' : 'pill-keep'">
                    {{ r.status }}
                  </span>
                </div>
                <div *ngIf="r.isQuota && r.pct != null" class="quota-bar-wrapper">
                  <app-quota-bar [pct]="r.pct" [height]="8"></app-quota-bar>
                  <div class="quota-subtext">
                    {{ formatNum(r.remaining) }} {{ r.unit }} left of {{ formatNum(r.quota) }} · {{ formatNum(r.pct) }}% landed
                  </div>
                </div>
              </div>
              <div class="quota-source-footnote">
                Source: {{ quotaSourceHost || 'From regulations server' }}
                <span *ngIf="quotaThrough"> · data through {{ quotaThrough }}</span>.
                <span *ngIf="hasQuotaAlert"> Alert line at 80%.</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Before You Keep Checklist -->
        <div *ngIf="hasChecklist" class="card-box checklist-card">
          <div class="section-label">Before you keep one</div>
          <div *ngIf="activeRule?.permit" class="check-item">
            <span class="material-icons check-icon">check_circle</span>
            <span class="check-text">{{ activeRule?.permit }}</span>
          </div>
          <div *ngIf="activeRule?.vesselLimit" class="check-item">
            <span class="material-icons check-icon">check_circle</span>
            <span class="check-text">{{ activeRule?.vesselLimit }}</span>
          </div>
          <div *ngIf="activeRule?.gear" class="check-item">
            <span class="material-icons check-icon">check_circle</span>
            <span class="check-text">{{ activeRule?.gear }}</span>
          </div>
          <div *ngFor="let note of activeRule?.notes" class="check-item">
            <span class="material-icons check-icon">check_circle</span>
            <span class="check-text">{{ note }}</span>
          </div>
        </div>

        <!-- Source Link -->
        <div class="source-link-container" *ngIf="activeRule?.source">
          <a
            [href]="activeRule?.source?.url"
            target="_blank"
            rel="noopener noreferrer"
            class="source-link"
          >
            Source: <span class="link-title">{{ activeRule?.source?.title }}</span>. {{ confirmText }}
          </a>
        </div>

        <!-- Related Topics Rows -->
        <div class="related-topics">
          <!-- Lookalike ID comparison -->
          <button
            *ngIf="species.lookalikes && species.lookalikes.length > 0"
            type="button"
            class="topic-row"
            (click)="onOpenId(species.lookalikes[0])"
          >
            <div class="topic-icon-wrap">
              <span class="material-icons">visibility</span>
            </div>
            <span class="topic-label">
              How do I tell it from {{ species.lookalikes[0].name.toLowerCase() }}?
            </span>
            <span class="material-icons topic-arrow">chevron_right</span>
          </button>

          <!-- Other Sector rules -->
          <button type="button" class="topic-row" (click)="onOpenOtherSector()">
            <div class="topic-icon-wrap">
              <span class="material-icons">work_outline</span>
            </div>
            <span class="topic-label">{{ otherSectorLabel }} rules</span>
            <span class="material-icons topic-arrow">chevron_right</span>
          </button>

          <!-- Paired Waters rules (state vs federal) -->
          <button *ngIf="pairedWaters" type="button" class="topic-row" (click)="onOpenPairedWaters(pairedWaters)">
            <div class="topic-icon-wrap">
              <span class="material-icons">explore</span>
            </div>
            <span class="topic-label">Fishing in {{ pairedWaters.lowerLabel }}?</span>
            <span class="material-icons topic-arrow">chevron_right</span>
          </button>

          <!-- Recent changes history -->
          <button *ngIf="isFollowing" type="button" class="topic-row" (click)="onOpenChanges()">
            <div class="topic-icon-wrap">
              <span class="material-icons">history</span>
            </div>
            <span class="topic-label">What changed recently?</span>
            <span class="material-icons topic-arrow">chevron_right</span>
          </button>
        </div>
      </div>

      <!-- Loading / Error State -->
      <ng-template #loadingOrError>
        <div class="center-content">
          <div *ngIf="error" class="error-view">
            <div class="skipper-says-card">
              <div class="skipper-content">
                <div class="skipper-text">{{ error }}</div>
                <button type="button" class="btn-retry" (click)="loadData()">Retry</button>
              </div>
            </div>
          </div>
          <div *ngIf="!error" class="loading-spinner">
            <span class="material-icons spin">refresh</span>
            <span>Checking regulations...</span>
          </div>
        </div>
      </ng-template>

      <!-- Bottom Ask Bar -->
      <div class="ask-bar-container">
        <form (ngSubmit)="submitAsk()" class="ask-form">
          <input
            type="text"
            [(ngModel)]="askText"
            name="askInput"
            placeholder="Ask about another fish or size…"
            class="ask-input"
            autocomplete="off"
          />
          <button type="submit" class="ask-submit-btn" [disabled]="!askText.trim()" title="Send">
            <span class="material-icons">arrow_upward</span>
          </button>
        </form>
        <div *ngIf="askReply" class="ask-reply-toast">
          {{ askReply }}
        </div>
      </div>

      <!-- Follow Preferences Modal -->
      <app-keep-it-follow-sheet
        *ngIf="showFollowSheet && species"
        [species]="species"
        [sector]="sector"
        (closed)="onFollowSheetClosed($event)"
      ></app-keep-it-follow-sheet>
    </div>
  `,
  styles: [`
    .answer-page {
      display: flex;
      flex-direction: column;
      height: 100%;
      background: #ffffff;
      color: #0F1C2E;
      position: relative;
      font-family: inherit;
    }

    .sheet-header {
      display: flex;
      align-items: center;
      padding: 12px 16px;
      border-bottom: 1px solid #E5E8EB;
      background: #ffffff;
      z-index: 2;
    }

    .round-btn {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: #F0F4F8;
      border: none;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #0F1C2E;
      cursor: pointer;
      transition: background 0.15s ease;
    }

    .round-btn:hover {
      background: #E2E8F0;
    }

    .header-titles {
      flex: 1;
      margin: 0 12px;
      overflow: hidden;
    }

    .header-titles .title {
      font-size: 17px;
      font-weight: 700;
      margin: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      color: #0F1C2E;
    }

    .header-titles .subtitle {
      font-size: 12px;
      color: #5A6E85;
      margin-top: 1px;
    }

    .header-actions {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .follow-pill {
      display: flex;
      align-items: center;
      gap: 5px;
      height: 34px;
      padding: 0 12px;
      border-radius: 17px;
      border: 1px solid #D5E0EB;
      background: #ffffff;
      color: #007AFF;
      font-size: 13.5px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.15s ease;
    }

    .follow-pill:hover {
      background: #F0F6FF;
    }

    .follow-pill.following {
      background: #007AFF;
      color: #ffffff;
      border-color: #007AFF;
    }

    .follow-pill .pill-icon {
      font-size: 16px;
    }

    .body-scroll {
      flex: 1;
      overflow-y: auto;
      padding: 16px 16px 24px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .user-bubble-container {
      display: flex;
      justify-content: flex-end;
    }

    .user-bubble {
      max-width: 82%;
      background: #007AFF;
      color: #ffffff;
      padding: 10px 15px;
      border-radius: 18px 18px 4px 18px;
      font-size: 15px;
      font-weight: 500;
      line-height: 1.35;
    }

    .skipper-says-card {
      display: flex;
      gap: 12px;
      padding: 12px 14px;
      background: #F7F9FC;
      border: 1px solid #E8EEF5;
      border-radius: 16px;
    }

    .skipper-avatar img {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      object-fit: cover;
    }

    .skipper-content {
      flex: 1;
    }

    .skipper-name {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      color: #007AFF;
      margin-bottom: 2px;
    }

    .skipper-text {
      font-size: 14.5px;
      color: #0F1C2E;
      line-height: 1.4;
    }

    .offer-actions {
      display: flex;
      gap: 10px;
      margin-top: 10px;
      align-items: center;
    }

    .btn-follow {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 7px 15px;
      border-radius: 18px;
      background: #007AFF;
      color: #ffffff;
      border: none;
      font-size: 13.5px;
      font-weight: 600;
      cursor: pointer;
    }

    .btn-not-now {
      background: transparent;
      border: none;
      color: #5A6E85;
      font-size: 13.5px;
      font-weight: 500;
      cursor: pointer;
      padding: 6px 10px;
    }

    .card-box {
      background: #ffffff;
      border: 1px solid #E5EAF0;
      border-radius: 18px;
      overflow: hidden;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    }

    .verdict-band {
      padding: 16px;
    }

    .verdict-keep {
      background: #E8F8F0;
    }

    .verdict-closed {
      background: #FDE8E8;
    }

    .verdict-release, .verdict-info {
      background: #F4F6F9;
    }

    .verdict-label {
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.8px;
      margin-bottom: 4px;
    }

    .verdict-keep .verdict-label { color: #1F9D61; }
    .verdict-closed .verdict-label { color: #D73333; }
    .verdict-release .verdict-label, .verdict-info .verdict-label { color: #5A6E85; }

    .big-verdict-row {
      display: flex;
      align-items: baseline;
      gap: 8px;
    }

    .big-number {
      font-size: 52px;
      font-weight: 800;
      line-height: 1;
      letter-spacing: -2px;
    }

    .verdict-keep .big-number { color: #166E44; }
    .verdict-closed .big-number { color: #D73333; }
    .verdict-release .big-number, .verdict-info .big-number { color: #0F1C2E; }

    .unit-text {
      font-size: 16px;
      font-weight: 600;
      color: #0F1C2E;
    }

    .verdict-message {
      font-size: 16px;
      font-weight: 600;
      color: #0F1C2E;
      margin-top: 4px;
    }

    .verdict-detail {
      font-size: 13.5px;
      color: #5A6E85;
      margin-top: 6px;
    }

    .amber-note {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      padding: 10px 14px;
      background: #FFF9E6;
      border-top: 1px solid #FFE7A3;
      color: #925F00;
      font-size: 13.5px;
      line-height: 1.35;
    }

    .amber-icon {
      font-size: 18px;
      color: #C68200;
    }

    .section-row {
      padding: 16px;
      border-top: 1px solid #EAEFF5;
    }

    .section-label {
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.5px;
      color: #5A6E85;
      text-transform: uppercase;
      margin-bottom: 10px;
    }

    .size-text-sub {
      font-size: 13px;
      color: #5A6E85;
      margin-top: 8px;
    }

    .periods-list {
      margin-top: 10px;
    }

    .period-item {
      display: flex;
      align-items: center;
      padding: 8px 0;
      border-top: 1px solid #F0F4F8;
      font-size: 13.5px;
      color: #5A6E85;
    }

    .period-item.is-now {
      font-weight: 600;
      color: #0F1C2E;
    }

    .period-dates {
      flex: 1.2;
    }

    .period-label {
      flex: 2;
      text-align: right;
    }

    .period-badges {
      display: flex;
      gap: 6px;
      margin-left: 8px;
    }

    .badge {
      font-size: 11px;
      font-weight: 700;
      padding: 2px 7px;
      border-radius: 10px;
    }

    .badge-closed {
      background: #FDE8E8;
      color: #D73333;
    }

    .badge-now {
      background: #E8F8F0;
      color: #1F9D61;
    }

    .quota-rows {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .quota-row-top {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 4px;
    }

    .quota-row-label {
      font-size: 14px;
      font-weight: 600;
      color: #0F1C2E;
    }

    .pill {
      font-size: 11px;
      font-weight: 700;
      padding: 3px 8px;
      border-radius: 12px;
    }

    .pill-keep {
      background: #E8F8F0;
      color: #1F9D61;
    }

    .pill-shut {
      background: #FDE8E8;
      color: #D73333;
    }

    .quota-subtext {
      font-size: 12px;
      color: #5A6E85;
      margin-top: 4px;
    }

    .quota-source-footnote {
      font-size: 12px;
      color: #8C9CAE;
      margin-top: 6px;
    }

    .checklist-card {
      padding: 16px;
    }

    .check-item {
      display: flex;
      gap: 10px;
      align-items: flex-start;
      margin-top: 10px;
    }

    .check-icon {
      font-size: 18px;
      color: #1F9D61;
      margin-top: 1px;
    }

    .check-text {
      font-size: 14px;
      color: #0F1C2E;
      line-height: 1.4;
    }

    .source-link-container {
      padding: 0 4px;
    }

    .source-link {
      font-size: 13px;
      color: #5A6E85;
      text-decoration: none;
      line-height: 1.4;
    }

    .source-link .link-title {
      color: #007AFF;
      font-weight: 500;
    }

    .related-topics {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-top: 6px;
    }

    .topic-row {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 14px;
      background: #ffffff;
      border: 1px solid #E5EAF0;
      border-radius: 14px;
      cursor: pointer;
      text-align: left;
      transition: all 0.15s ease;
    }

    .topic-row:hover {
      background: #F8FAFD;
      border-color: #D2DFEE;
    }

    .topic-icon-wrap {
      width: 32px;
      height: 32px;
      border-radius: 8px;
      background: #F0F4F8;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #007AFF;
    }

    .topic-icon-wrap .material-icons {
      font-size: 18px;
    }

    .topic-label {
      flex: 1;
      font-size: 14px;
      font-weight: 600;
      color: #0F1C2E;
    }

    .topic-arrow {
      color: #A0B2C6;
      font-size: 20px;
    }

    .ask-bar-container {
      padding: 8px 16px 12px;
      background: #ffffff;
      border-top: 1px solid #EAEFF5;
    }

    .ask-form {
      display: flex;
      align-items: center;
      background: #F0F4F8;
      border-radius: 22px;
      padding: 4px 6px 4px 14px;
    }

    .ask-input {
      flex: 1;
      background: transparent;
      border: none;
      outline: none;
      font-size: 15px;
      color: #0F1C2E;
    }

    .ask-submit-btn {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: #007AFF;
      border: none;
      color: #ffffff;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }

    .ask-submit-btn:disabled {
      background: #B0C8E8;
      cursor: not-allowed;
    }

    .ask-reply-toast {
      margin-top: 8px;
      padding: 8px 12px;
      border-radius: 8px;
      background: #FFF9E6;
      border: 1px solid #FFE7A3;
      color: #925F00;
      font-size: 13px;
    }

    .center-content {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
    }

    .loading-spinner {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      color: #5A6E85;
      font-size: 14px;
    }

    .spin {
      animation: spin 1s linear infinite;
      font-size: 28px;
    }

    @keyframes spin {
      100% { transform: rotate(360deg); }
    }

    .btn-retry {
      margin-top: 10px;
      padding: 6px 14px;
      border-radius: 14px;
      background: #007AFF;
      color: #ffffff;
      border: none;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
    }
  `],
})
export class KeepItAnswerPageComponent implements OnInit, OnDestroy {
  @Input() speciesId!: string;
  @Input() sector: Sector = 'recreational';
  @Input() waters: Waters = Waters.nj;
  @Input() question?: string;
  @Input() askedInches?: number;
  @Input() intro?: string;

  @Output() back = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();
  @Output() openRelated = new EventEmitter<{
    speciesId?: string;
    sector?: Sector;
    waters?: Waters;
    question: string;
    askedInches?: number;
    intro?: string;
  }>();
  @Output() openIdPage = new EventEmitter<{ species: Species; lookalike: Lookalike }>();
  @Output() openChangesPage = new EventEmitter<{ species: Species; sector: Sector }>();

  private api = inject(RegsApiService);
  private followsService = inject(RegsFollowService);
  private choiceService = inject(RegsChoiceService);
  private cdr = inject(ChangeDetectorRef);

  species: Species | null = null;
  liveData: LiveData | null = null;
  error: string | null = null;

  offerDismissed = false;
  followFeedback: string | null = null;
  showFollowSheet = false;

  askText = '';
  askReply: string | null = null;

  confirmText = regsConfirmText;
  private subs = new Subscription();

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get isFollowing(): boolean {
    return this.species ? this.followsService.isFollowing(this.species.id, this.sector) : false;
  }

  get defaultQuestion(): string {
    if (!this.species) return 'Can I keep this fish today?';
    const fish = this.species.short.toLowerCase();
    const article = 'aeiou'.includes(fish.charAt(0)) ? 'an' : 'a';
    return `Can I keep ${article} ${fish} today?`;
  }

  get activeRule(): Rule | null {
    return this.species?.rule(this.sector, this.waters) ?? null;
  }

  get currentVerdict(): Verdict {
    if (!this.species || !this.activeRule) {
      return {
        kind: 'info',
        label: 'NO RULES',
        unit: '',
        detail: '',
        says: 'I don’t have rules for this fish here.',
      };
    }
    return verdictFor(this.species, this.activeRule, this.waters, this.species.todayMd);
  }

  get verdictSays(): string {
    if (!this.species || !this.activeRule) {
      const secLabel = this.sector === 'commercial' ? 'commercial' : 'recreational';
      return `I don’t have ${secLabel} rules for ${this.species?.short || 'this fish'} in ${this.waters.label}.`;
    }
    if (this.askedInches != null) {
      return answerSize(this.species, this.activeRule, this.waters, this.askedInches);
    }
    return this.currentVerdict.says;
  }

  get areaSays(): string | null {
    if (!this.species || !this.activeRule) return null;
    const here = this.choiceService.here();
    if (here && here.place === this.waters && here.water) {
      const area = this.activeRule.areaRule(here.water);
      if (area) {
        const inArea = this.askedInches != null
          ? answerSize(this.species, area, this.waters, this.askedInches)
          : verdictFor(this.species, area, this.waters, this.species.todayMd).says;
        if (inArea && inArea !== this.verdictSays) {
          return `You look to be in ${here.waterName}, where the rule is different: ${inArea} Confirm which waters you’re in before you keep it.`;
        }
      }
    }
    return null;
  }

  get verdictBandClass(): string {
    switch (this.currentVerdict.kind) {
      case 'keep': return 'verdict-keep';
      case 'closed': return 'verdict-closed';
      case 'release': return 'verdict-release';
      default: return 'verdict-info';
    }
  }

  get verdictFallbackText(): string {
    switch (this.currentVerdict.kind) {
      case 'keep': return this.currentVerdict.unit || 'Keep';
      case 'closed': return 'Don’t keep it today';
      case 'release': return 'Release it';
      default: return this.currentVerdict.detail || 'Check official rules';
    }
  }

  get hasSizeLimits(): boolean {
    if (!this.activeRule) return false;
    return !!SizeLimit.parse(this.activeRule.quick.min) || !!SizeLimit.parse(this.activeRule.quick.max);
  }

  get minLimit(): SizeLimit | null {
    return SizeLimit.parse(this.activeRule?.quick.min);
  }

  get maxLimit(): SizeLimit | null {
    return SizeLimit.parse(this.activeRule?.quick.max);
  }

  get sizeDescription(): string | null {
    if (!this.activeRule) return null;
    return this.activeRule.slot || this.activeRule.minSize || this.activeRule.maxSize || null;
  }

  get showWeekStrip(): boolean {
    if (!this.activeRule) return false;
    return (
      this.activeRule.noRetentionDays.length > 0 &&
      (this.currentVerdict.kind === 'keep' || !!this.activeRule.today.noRetention)
    );
  }

  get njTodayDate(): Date {
    const md = this.species?.todayMd || '10-01';
    const now = new Date();
    const m = parseInt(md.substring(0, 2), 10) || now.getMonth() + 1;
    const d = parseInt(md.substring(3), 10) || now.getDate();
    return new Date(now.getFullYear(), m - 1, d);
  }

  get sortedSeasons(): Season[] {
    if (!this.activeRule?.seasons) return [];
    return [...this.activeRule.seasons].sort((a, b) => a.from.localeCompare(b.from));
  }

  isSeasonNow(s: Season): boolean {
    const md = this.species?.todayMd;
    if (!md) return false;
    if (s.from.localeCompare(s.to) <= 0) {
      return md.localeCompare(s.from) >= 0 && md.localeCompare(s.to) <= 0;
    } else {
      return md.localeCompare(s.from) >= 0 || md.localeCompare(s.to) <= 0;
    }
  }

  formatSeasonDates(s: Season): string {
    return `${mdLabel(s.from)} – ${mdLabel(s.to)}`;
  }

  get quotaSourceHost(): string | null {
    if (!this.liveData?.sources.length) return null;
    try {
      const u = new URL(this.liveData.sources[0].url);
      return u.hostname.replace(/^www\./, '');
    } catch {
      return null;
    }
  }

  get quotaThrough(): string | null {
    return this.liveData?.sources.find((s) => s.dataThrough)?.dataThrough ?? null;
  }

  get hasQuotaAlert(): boolean {
    return this.liveData?.rows.some((r) => r.isQuota && r.pct != null) ?? false;
  }

  isClosedStatus(status: string): boolean {
    return /clos/i.test(status);
  }

  formatNum(v: number | null | undefined): string {
    if (v == null) return '?';
    return v >= 100 || v % 1 === 0 ? Math.round(v).toString() : v.toFixed(1);
  }

  get hasChecklist(): boolean {
    if (!this.activeRule) return false;
    return !!(
      this.activeRule.permit ||
      this.activeRule.vesselLimit ||
      this.activeRule.gear ||
      (this.activeRule.notes && this.activeRule.notes.length > 0)
    );
  }

  get otherSectorLabel(): string {
    return this.sector === 'recreational' ? 'Commercial' : 'Recreational';
  }

  get pairedWaters(): Waters | null {
    if (!this.species) return null;
    const p = regsPair(this.waters, this.choiceService.here());
    return p && this.species.rule(this.sector, p) ? p : null;
  }

  async loadData(): Promise<void> {
    this.error = null;
    try {
      const sp = await this.api.species(this.speciesId);
      this.species = sp;
      if (sp.hasLive(this.sector)) {
        try {
          this.liveData = await this.api.live(sp.id, this.sector);
        } catch (_) {}
      }
      this.cdr.markForCheck();
    } catch (_) {
      this.error = 'Couldn’t load rules for this fish right now.';
      this.cdr.markForCheck();
    }
  }

  onBack(): void {
    this.back.emit();
  }

  onClose(): void {
    this.close.emit();
  }

  openFollowModal(): void {
    this.showFollowSheet = true;
  }

  onFollowSheetClosed(result: FollowResult | 'cancelled'): void {
    this.showFollowSheet = false;
    if (result === 'followed' || result === 'followedNoPush') {
      const watch = this.liveData?.hasQuotaNumbers ? 'the rules, season or quota change' : 'the rules or season change';
      this.followFeedback = this.currentVerdict.kind === 'closed'
        ? `You’re set. I’ll tell you when ${this.species?.short.toLowerCase()} reopens, or if rules change.`
        : `You’re set. I’ll tell you when ${watch}.`;
    } else if (result === 'unfollowed') {
      this.followFeedback = `OK, I’ve stopped watching ${this.species?.short.toLowerCase()} for you.`;
    }
    this.cdr.markForCheck();
  }

  dismissOffer(): void {
    this.offerDismissed = true;
  }

  onOpenId(lookalike: Lookalike): void {
    if (!this.species) return;
    this.openIdPage.emit({ species: this.species, lookalike });
  }

  onOpenOtherSector(): void {
    const other: Sector = this.sector === 'recreational' ? 'commercial' : 'recreational';
    const label = other === 'commercial' ? 'commercial' : 'recreational';
    this.openRelated.emit({
      sector: other,
      question: `What are the ${label} rules for ${this.species?.short.toLowerCase()}?`,
    });
  }

  onOpenPairedWaters(other: Waters): void {
    if (!this.species) return;
    const note = this.species.watersNote(other);
    const here = this.choiceService.here()?.waters;
    const where = here && here !== other ? ` You’re in ${here.lowerLabel} now; these are the rules for when you’re in ${other.lowerLabel}.` : '';
    const intro = note || where ? `${note ? `${other.label}: ${note}` : ''}${where}`.trim() : undefined;

    this.openRelated.emit({
      waters: other,
      question: `What if I’m fishing in ${other.lowerLabel}?`,
      intro,
    });
  }

  onOpenChanges(): void {
    if (!this.species) return;
    this.openChangesPage.emit({ species: this.species, sector: this.sector });
  }

  async submitAsk(): Promise<void> {
    const q = this.askText.trim();
    if (!q) return;

    try {
      const idx = await this.api.index();
      let match = parseAsk(q, idx.species);
      let id = match?.species.id;
      let inches = match?.inches;

      if (!id) {
        const sizeMatch = q.match(/(\d+(?:\.\d+)?)\s*(?:"|”|in\b|inch(?:es)?\b)/i);
        if (sizeMatch && this.species) {
          id = this.species.id;
          inches = parseFloat(sizeMatch[1]);
        }
      }

      if (!id) {
        this.askReply = 'I don’t know that one yet. Try a name like “fluke” or “bluefin”, or a size like “14 inches”.';
        this.cdr.markForCheck();
        return;
      }

      this.askText = '';
      this.askReply = null;
      this.openRelated.emit({
        speciesId: id,
        question: q,
        askedInches: inches ?? undefined,
      });
    } catch (_) {}
  }

  avatarFallback(event: Event): void {
    const target = event.target as HTMLElement;
    target.style.display = 'none';
  }
}
